// Assignments — three screens for who holds what: Admin, Accounting
// Assignments, Approval Assignments. Any role-holder may open the tab; each
// screen shows for the people who hold something on it, read-only where they
// may not appoint (`visibleAssignmentScreens` in `lib/identity.ts` decides
// which screens a session sees; `App.tsx` gates the tab itself on that list
// being non-empty).
//
//   Admin                 — the Admin group. Admin-only: nobody else has
//                            anything to do here, so nobody else sees it.
//   Accounting Assignments — Import Control, Export Control, Closing
//                            Confirmation, Manual Entry, Denial Review,
//                            References. Internal-role groups.
//   Approval Assignments  — one group per company: its director, department
//                            heads, fleet maintainers AND its oversight seat
//                            all together. Assignment-row groups. Visible to
//                            ANY active-assignment holder, role-agnostic —
//                            including a fleet-only seat, whose company group
//                            lives here even though `fleet` is not an approval
//                            tier. Oversight Approval no longer gets its own
//                            standalone box (issue 2, 2026-08-10) — it is
//                            company-scoped now, exactly like director and
//                            department-head, and joins each company's group.
//
// EVERY GROUP ON A VISIBLE SCREEN RENDERS, appoint authority or not — a
// group without it just renders with no Appoint control. That is new: the
// page used to hide any group the viewer could not appoint to, which is
// wrong once holding a role is what opens the tab rather than appointing
// being the price of admission.
//
// Two mechanisms sit under the groups and the page deliberately hides the
// difference — a group is a group:
//   * COMPANY groups are `assignments` rows. The appointment row IS the
//     authority; there is nothing else to keep in step with it.
//   * Every other group is an internal step role (`user_roles`), granted and
//     revoked directly.
// Both go through the API and both return synchronously.
//
// Who may appoint where mirrors the API's own check, so the page never offers
// a control the API would refuse:
//   * Admin may appoint anyone to any group, Admin included.
//   * An active director may appoint or retire DEPARTMENT HEADS and FLEET
//     MAINTAINERS, and only for the company their assignment names.
//     Company-exact: `Other` is one company, not a wildcard.
//   * An active oversight seat may appoint DIRECTOR and DEPARTMENT HEAD, and
//     retire DEPARTMENT HEAD and FLEET, for its own company only. The two
//     lists differ BY ACTION on purpose: oversight may seat a director but may
//     never retire one, because a seat that could do both could depose the
//     authority only Admin is allowed to remove (`canAppoint`'s docstring in
//     `backend/src/domain/authority.ts` is the reason, and the rule this
//     mirrors). Creating an OVERSIGHT seat stays Admin-only.
//   * Nobody else sees an Appoint control.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  Option,
  Spinner,
  Subtitle2,
  Tab,
  TabList,
  Toast,
  ToastTitle,
  makeStyles,
  mergeClasses,
  tokens,
  useToastController,
} from '@fluentui/react-components'
import { DeleteRegular, PersonAddRegular, WarningRegular } from '@fluentui/react-icons'
import { errorMessage } from '../api/errors'
import { ScreenTitle } from '../components/ScreenTitle'
import { ErrorBanner } from '../components/States'
import { EntityPicker, type EntityOption } from '../components/EntityPicker'
import {
  ACCOUNTING_STEP_GROUPS,
  ADMIN_STEP_GROUPS,
  APPROVAL_ASSIGNMENT_ROLES,
  ASSIGNMENT_ACTION,
  ASSIGNMENT_ROLES,
  ASSIGNMENT_STATUS,
  GRANTABLE_ROLES,
  TOASTER_ID,
  assignmentRoleLabel,
  tokenForRole,
  type AssignmentRole,
  type Role,
  type StepGroupSpec,
  type Workspace,
} from '../lib/constants'
import type { Assignment, DirectoryUser, UserRole } from '../lib/types'
import { visibleAssignmentScreens, type AssignmentScreen, type Session } from '../lib/identity'
import { PENDING, pageState, type ReadState } from '../lib/readState'
import {
  createAssignment,
  grantUserRole,
  listAssignments,
  listUserRoles,
  loadCompaniesForManagement,
  revokeUserRole,
  searchPeople,
} from '../lib/data'

/** The one workspace. Kept as a constant rather than a control (a one-option
    dropdown is furniture), but the row filter stays so a historical row for
    another workspace is still excluded. */
const WS: Workspace = 'invoice'

const SCREEN_TITLE: Record<AssignmentScreen, string> = {
  admin: 'Admin',
  accounting: 'Accounting Assignments',
  approval: 'Approval Assignments',
}

// A stable reference for "not loaded yet" — a fresh `[]` literal at the call
// site would be a new array every render, which is what fed a `useMemo`
// dependency array below and defeated its memoization.
const EMPTY_COMPANIES: { id: string; name: string }[] = []

const useStyles = makeStyles({
  // Title + subheader on one baseline, as every pipeline screen does it.
  header: { display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' },
  sub: { color: tokens.colorNeutralForeground3 },
  screenTabs: { marginBottom: '16px' },
  controls: { display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '24px' },
  search: { minWidth: '280px' },
  spacer: { flex: 1 },
  groups: { display: 'flex', flexDirection: 'column', gap: '14px' },
  group: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '12px 16px',
  },
  groupHead: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
  groupTitle: { flexShrink: 0 },
  blurb: { color: tokens.colorNeutralForeground3, display: 'block', marginTop: '2px', marginBottom: '10px' },
  // A wrapping warning line under the group head (mirrors States.tsx
  // warningBanner's colours without the full banner chrome).
  warn: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    color: tokens.colorPaletteYellowForeground2,
    marginTop: '4px',
    marginBottom: '8px',
  },
  warnIcon: { flexShrink: 0, marginTop: '2px' },
  count: { color: tokens.colorNeutralForeground3 },
  members: { display: 'flex', flexDirection: 'column', gap: '6px' },
  member: {
    display: 'grid',
    gridTemplateColumns: 'minmax(160px, 220px) minmax(0, 1fr) auto auto',
    alignItems: 'center',
    columnGap: '12px',
    padding: '6px 10px',
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  memberInactive: { opacity: 0.6 },
  email: { color: tokens.colorNeutralForeground3, overflow: 'hidden', textOverflow: 'ellipsis' },
  reason: { color: tokens.colorNeutralForeground3, display: 'block' },
  trash: { color: tokens.colorBrandForeground1 },
  none: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  hint: { color: tokens.colorNeutralForeground3, display: 'block', marginTop: '4px' },
  // `overflow: visible` matters: the staff picker's listbox renders INLINE (it
  // must stay inside the dialog's focus trap), so a clipping ancestor cuts the
  // dropdown off instead of letting it overlay. The form is two fields tall, so
  // nothing here needs to scroll.
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minWidth: '360px',
    overflow: 'visible',
  },
})

type ShowFilter = 'current' | 'all'

/** One line in a group: an internal-role holder, or an appointment. */
interface Seat {
  key: string
  name: string
  email?: string
  /** Company groups only — Oversight approver / Director / Department head /
      Fleet. There is no separate Oversight group any more: its seat sits in the
      company group with the rest (issue 2, 2026-08-10). */
  roleLabel?: string
  /** Badge for anything that is not a live seat (Retired / Rejected). */
  status?: string
  rejectionReason?: string
  /** False for a retired/rejected row, which is history, not authority. */
  live: boolean
  canRemove: boolean
  remove?: () => void
}

/** What an Appoint control on a group will do when submitted. */
type Target =
  | { kind: 'role'; role: Role; title: string }
  | {
      kind: 'assignment'
      roleChoices: AssignmentRole[]
      companyId?: string
      title: string
    }

interface Group {
  key: string
  title: string
  blurb?: string
  seats: Seat[]
  /** Absent when the signed-in user may not appoint here. The group still
      renders — see the file header. */
  target?: Target
  /** A company with nobody in it is called out rather than hidden. Set only
      on a group the viewer may appoint to — see `companyGroups`' comment
      below for why. */
  warnEmpty?: string
}

function statusBadge(status: string) {
  const color = status === ASSIGNMENT_STATUS.rejected ? 'danger' : 'informative'
  return (
    <Badge appearance="tint" color={color as 'danger' | 'informative'}>
      {status}
    </Badge>
  )
}

/** A live appointment: applied, not retired, still active. */
function isLive(a: Assignment): boolean {
  return !!a.is_active && !a.retired_at
}

/** The status to show: a retired row reads "Retired" whatever else it says. */
function displayStatus(a: Assignment): string {
  return a.retired_at ? 'Retired' : a.request_status
}

interface Props {
  session: Session
}

export function AssignmentsScreen({ session }: Props) {
  const s = useStyles()
  const { dispatchToast } = useToastController(TOASTER_ID)

  const visibleScreens = useMemo(() => visibleAssignmentScreens(session), [session])
  // `null` only ever surfaces transiently, IF at all: `session` is set once,
  // in App.tsx's mount effect, and nothing today replaces it for a mounted
  // component, so `visibleScreens` is referentially stable for this
  // component's whole lifetime and the re-sync effect below never actually
  // fires. It stays defensive rather than being deleted: wiring a session
  // re-derive in later (a role change without a new sign-in, say) must not
  // strand this component on a screen the new session no longer holds
  // anything on, and this is what would catch that the day it happens.
  const [activeScreen, setActiveScreen] = useState<AssignmentScreen | null>(visibleScreens[0] ?? null)

  // Re-sync whenever the visible set changes and the active screen falls
  // outside it — otherwise a TabList could sit with `selectedValue` matching
  // no `Tab`. Defensive only today (see the note above); this is what makes
  // that safe to add later without a second thought here.
  useEffect(() => {
    if (activeScreen !== null && visibleScreens.includes(activeScreen)) return
    setActiveScreen(visibleScreens[0] ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleScreens])

  const [search, setSearch] = useState('')
  const [show, setShow] = useState<ShowFilter>('current')

  const [assignmentsState, setAssignmentsState] = useState<ReadState<Assignment[]>>(PENDING)
  const [userRolesState, setUserRolesState] = useState<ReadState<UserRole[]>>(PENDING)
  // Approval Assignments' THIRD independent read — the company master — held
  // as a `ReadState` exactly like the other two. It used to be a plain array
  // defaulted on failure with `.catch(() => setCompanies([]))`, which is a
  // false EMPTY, not a modeled failure: a transient `GET /companies` error
  // left a director's own company silently missing, reporting `ready` with
  // no error and no retry — precisely what the three-state model exists to
  // rule out.
  const [companiesState, setCompaniesState] = useState<ReadState<{ id: string; name: string }[]>>(
    PENDING,
  )
  // Write-action failures (revoke, retire, appoint) — a transient banner, kept
  // separate from the READ states above, which replace the groups area
  // itself rather than merely bannering over it.
  const [error, setError] = useState<string | null>(null)
  const [busyKey, setBusyKey] = useState<string | null>(null)

  // Appoint dialog.
  const [target, setTarget] = useState<Target | null>(null)
  // The dialog surface element, held in STATE rather than a ref because the
  // picker below has to re-render once it exists — it mounts its listbox there,
  // which is what keeps the options above the dialog's own fields.
  const [dialogSurface, setDialogSurface] = useState<HTMLDivElement | null>(null)
  const [apRole, setApRole] = useState<AssignmentRole>(ASSIGNMENT_ROLES.departmentHead)
  const [apUser, setApUser] = useState<DirectoryUser | null>(null)
  // The last directory search's results. An appointment carries the user
  // principal name as well as the id, so the picked option is resolved back to
  // its whole record; a ref because it is lookup data, not rendered state.
  const peopleRef = useRef<DirectoryUser[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [dialogError, setDialogError] = useState<string | null>(null)

  // ---- Authority (mirrors the API's own check) -------------------------------
  const isAdmin = (session.rolesByWs[WS] ?? []).includes('Admin')
  const myDirectorships = useMemo(
    () => session.assignments.filter((a) => a.role === ASSIGNMENT_ROLES.director && a.workspace === WS),
    [session.assignments],
  )
  /** Company-exact. A director of `Other` directs `Other` and nothing else — it
      is one company among the rest, never a superuser seat. */
  const directsCompany = useCallback(
    (companyId: string) => !!companyId && myDirectorships.some((a) => a.companyId === companyId),
    [myDirectorships],
  )
  const myOversights = useMemo(
    () => session.assignments.filter((a) => a.role === ASSIGNMENT_ROLES.oversight && a.workspace === WS),
    [session.assignments],
  )
  /** Company-exact, exactly like `directsCompany` — `canAppoint` withholds the
      catch-all expansion from BOTH tiers on the appointment path, so an
      oversight seat on `Other` appoints within `Other` and nowhere else, even
      though the same seat decides for the anchor's member companies. */
  const oversightsCompany = useCallback(
    (companyId: string) => !!companyId && myOversights.some((a) => a.companyId === companyId),
    [myOversights],
  )

  const reloadAssignments = useCallback(async () => {
    try {
      const data = await listAssignments()
      setAssignmentsState({ kind: 'loaded', data })
    } catch (e) {
      setAssignmentsState({ kind: 'failed', error: `Could not load assignments: ${errorMessage(e)}` })
    }
  }, [])

  const reloadUserRoles = useCallback(async () => {
    try {
      const data = await listUserRoles()
      setUserRolesState({ kind: 'loaded', data })
    } catch (e) {
      setUserRolesState({ kind: 'failed', error: `Could not load roles: ${errorMessage(e)}` })
    }
  }, [])

  const reloadCompanies = useCallback(async () => {
    try {
      const cs = await loadCompaniesForManagement()
      setCompaniesState({
        kind: 'loaded',
        data: cs
          .filter((c) => c.is_active)
          .map((c) => ({ id: c.company_id, name: c.company_name }))
          .filter((c) => c.name)
          .sort((a, b) => a.name.localeCompare(b.name)),
      })
    } catch (e) {
      setCompaniesState({ kind: 'failed', error: `Could not load companies: ${errorMessage(e)}` })
    }
  }, [])

  // Fetch the roster ONLY when a screen backed by it is visible (Admin or
  // Accounting) — an assignment-only holder (director, department-head,
  // oversight, fleet) never needs `user_roles` at all, and the server refuses
  // them the read besides (`canViewUserRoleRoster`). Fetch the assignment
  // data (and the company master it is grouped by) only when Approval
  // Assignments is visible.
  const needsRoster = visibleScreens.includes('admin') || visibleScreens.includes('accounting')
  const needsApproval = visibleScreens.includes('approval')

  useEffect(() => {
    if (needsRoster) reloadUserRoles()
    if (needsApproval) {
      reloadAssignments()
      reloadCompanies()
    }
  }, [needsRoster, needsApproval, reloadUserRoles, reloadAssignments, reloadCompanies])

  const toast = (title: string, intent: 'success' | 'error' = 'success') =>
    dispatchToast(<Toast><ToastTitle>{title}</ToastTitle></Toast>, { intent })

  // ---- Writes ---------------------------------------------------------------

  async function revoke(row: UserRole) {
    setBusyKey(row.id)
    setError(null)
    try {
      await revokeUserRole(row.id)
      await reloadUserRoles()
      toast('Removed')
    } catch (e) {
      setError(`Remove failed: ${errorMessage(e)}`)
    } finally {
      setBusyKey(null)
    }
  }

  /** Retiring never deletes: the row is stamped with its retirement time and
      kept, because the audit trail is the point. */
  async function retire(a: Assignment) {
    setBusyKey(a.id)
    setError(null)
    try {
      await createAssignment({
        workspace: WS,
        action: ASSIGNMENT_ACTION.retire,
        role: a.role,
        company_id: a.company_id ?? null,
        user_id: a.user_id,
        user_upn: a.user_upn,
        user_name: a.user_name ?? null,
        target_assignment_id: a.id,
      })
      await reloadAssignments()
      toast('Retired')
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusyKey(null)
    }
  }

  function openAppoint(t: Target) {
    setDialogError(null)
    setApUser(null)
    if (t.kind === 'assignment') setApRole(t.roleChoices[0])
    setTarget(t)
  }

  async function submitAppoint() {
    if (!target) return
    if (!apUser) {
      setDialogError('Pick a person.')
      return
    }
    setDialogError(null)
    setSubmitting(true)
    try {
      if (target.kind === 'role') {
        const token = tokenForRole(target.role)
        if (!token) throw new Error(`${target.title} cannot be granted here.`)
        await grantUserRole({
          role: token,
          user_id: apUser.id,
          user_upn: apUser.upn,
          user_name: apUser.display_name,
          user_email: apUser.mail ?? apUser.upn,
        })
        await reloadUserRoles()
      } else {
        await createAssignment({
          workspace: WS,
          action: ASSIGNMENT_ACTION.appoint,
          role: apRole,
          company_id: target.companyId ?? null,
          user_id: apUser.id,
          user_upn: apUser.upn,
          user_name: apUser.display_name,
        })
        await reloadAssignments()
      }
      setTarget(null)
      toast(`Added to ${target.title}`)
    } catch (e) {
      // A refusal is the API's own sentence — show it verbatim.
      setDialogError(errorMessage(e))
    } finally {
      setSubmitting(false)
    }
  }

  // ---- Group assembly -------------------------------------------------------

  const userRoles = userRolesState.kind === 'loaded' ? userRolesState.data : null
  const assignmentRows = assignmentsState.kind === 'loaded' ? assignmentsState.data : null
  const companies = companiesState.kind === 'loaded' ? companiesState.data : EMPTY_COMPANIES

  const roleRows = useMemo(
    () => (userRoles ?? []).filter((r) => r.workspace === WS && r.is_active !== false),
    [userRoles],
  )

  const roleGroup = useCallback(
    (spec: StepGroupSpec): Group => {
      const token = tokenForRole(spec.role)
      const holders = token ? roleRows.filter((r) => r.role === token) : []
      return {
        key: `role:${spec.role}`,
        title: spec.title,
        blurb: spec.blurb,
        seats: holders
          .map((r) => ({
            key: r.id,
            name: r.user_name || r.user_upn || r.user_object_id,
            email: r.user_email || r.user_upn,
            live: true,
            canRemove: isAdmin,
            remove: () => revoke(r),
          }))
          .sort((x, y) => x.name.localeCompare(y.name)),
        target:
          isAdmin && token && GRANTABLE_ROLES.includes(spec.role)
            ? { kind: 'role', role: spec.role, title: spec.title }
            : undefined,
      }
    },
    // `revoke` is recreated every render; the seats it closes over are the rows,
    // which are in the dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roleRows, isAdmin],
  )

  const rows = useMemo(
    () => (assignmentRows ?? []).filter((a) => a.workspace === WS && a.action === ASSIGNMENT_ACTION.appoint),
    [assignmentRows],
  )

  const assignmentSeats = useCallback(
    (forRows: Assignment[]): Seat[] =>
      forRows
        .filter((a) => (show === 'all' ? true : isLive(a)))
        .map((a) => {
          // A director may retire the seats they may appoint — department heads
          // and fleet maintainers for their own company — never another director.
          // An oversight seat retires the SAME two roles for its own company,
          // and that list is deliberately NOT the list it may appoint: it may
          // seat a director but never retire one (`canAppoint`). The two retire
          // sets coincide, so one predicate serves both arms; the appoint sets
          // do not, which is why `roleChoices` below keeps them apart.
          const delegateMayRetire =
            a.role === ASSIGNMENT_ROLES.departmentHead || a.role === ASSIGNMENT_ROLES.fleet
          const seatCompanyId = a.company_id ?? ''
          const live = isLive(a)
          return {
            key: a.id,
            name: a.user_name ?? a.user_upn ?? '(unknown)',
            email: a.user_upn,
            roleLabel: assignmentRoleLabel(a.role),
            status: live ? undefined : displayStatus(a),
            rejectionReason: a.rejection_reason ?? undefined,
            live,
            canRemove:
              live &&
              (isAdmin ||
                (delegateMayRetire &&
                  (directsCompany(seatCompanyId) || oversightsCompany(seatCompanyId)))),
            remove: () => retire(a),
          }
        })
        // Live seats first, then by role label, then by name.
        .sort(
          (x, y) =>
            Number(y.live) - Number(x.live) ||
            (x.roleLabel ?? '').localeCompare(y.roleLabel ?? '') ||
            x.name.localeCompare(y.name),
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [show, isAdmin, directsCompany, oversightsCompany],
  )

  // Every active company in the master gets its own group, ordered by name.
  // There is no catch-all: authority keys on the company id, so the `Other`
  // stand-in for companies without a dedicated team went with the teams.
  //
  // Oversight Approval joins each company's group (issue 2, 2026-08-10) — it is
  // company-scoped now, exactly like director and department-head, and no
  // longer gets its own standalone box. Creating a NEW oversight seat stays
  // Admin-only (only Admin may ever create one), so `oversight` is offered as a
  // TARGET only on the admin branch below — but holding an oversight seat is
  // now itself appoint authority for that company, mirroring `canAppoint`.
  const companyGroups: Group[] = useMemo(
    () =>
      companies.map((c) => {
        const forCompany = rows.filter((a) => (a.company_id ?? '') === c.id)
        const directsHere = directsCompany(c.id)
        const oversightHere = oversightsCompany(c.id)
        const mayAppoint = isAdmin || directsHere || oversightHere
        // Fleet maintenance sits beside the approval seats (one group per
        // company, one Appoint control, the role picked in the dialog). It is
        // not an approval tier, so a director may appoint one just as they may a
        // department head.
        //
        // The non-admin branch is a UNION, because one person can hold both
        // seats on the same company: a director contributes department head and
        // fleet, an oversight seat contributes director and department head.
        // Oversight does NOT contribute fleet — `canAppoint`'s
        // OVERSIGHT_APPOINT_DELEGABLE is `director` and `department-head` only,
        // and offering a control the API refuses is the thing this mirror
        // exists to prevent. Built in tier order so the dialog's picker reads
        // the same way for everyone.
        const nonAdminChoices = ([] as AssignmentRole[]).concat(
          oversightHere ? [ASSIGNMENT_ROLES.director] : [],
          directsHere || oversightHere ? [ASSIGNMENT_ROLES.departmentHead] : [],
          directsHere ? [ASSIGNMENT_ROLES.fleet] : [],
        )
        const roleChoices: AssignmentRole[] = isAdmin
          ? [
              ASSIGNMENT_ROLES.oversight,
              ASSIGNMENT_ROLES.director,
              ASSIGNMENT_ROLES.departmentHead,
              ASSIGNMENT_ROLES.fleet,
            ]
          : nonAdminChoices
        // Every approval tier is present-if-staffed, so a company with
        // department heads and no director still gets a company-level approval.
        // Only a company with NO approval assignment at all releases from Import
        // Control straight past its own approvals — a fleet maintainer is not an
        // approver — so the warning keys on that, not on the director seat.
        const hasApprover = forCompany.some((a) => isLive(a) && APPROVAL_ASSIGNMENT_ROLES.includes(a.role))
        return {
          key: `company:${c.id}`,
          title: c.name,
          seats: assignmentSeats(forCompany),
          target: mayAppoint ? { kind: 'assignment', roleChoices, companyId: c.id, title: c.name } : undefined,
          // Only on a group the viewer may appoint to (`mayAppoint`) — every
          // OTHER visible-screen holder (a fleet maintainer, say) now sees
          // every company's card, and warning them about ~30 companies they
          // cannot staff would be noise, not information.
          warnEmpty:
            mayAppoint && !hasApprover
              ? 'This company has no approvers. Invoices for this company will move to payment without any company-level approvals.'
              : undefined,
        }
      }),
    [companies, rows, isAdmin, directsCompany, oversightsCompany, assignmentSeats],
  )

  const adminGroups = useMemo(() => ADMIN_STEP_GROUPS.map(roleGroup), [roleGroup])
  const accountingGroups = useMemo(() => ACCOUNTING_STEP_GROUPS.map(roleGroup), [roleGroup])
  // Approval Assignments: the per-company groups only. Oversight Approval no
  // longer gets its own standalone box (issue 2, 2026-08-10) — it is
  // company-scoped now and joins each company's group above. Not a closed
  // list — see docs/questions.md §4.3 ("Approval Assignments' contents are not
  // a closed list") for what a follow-on task would change and why it isn't
  // named yet.
  const approvalGroups = companyGroups

  const groupsByScreen: Record<AssignmentScreen, Group[]> = {
    admin: adminGroups,
    accounting: accountingGroups,
    approval: approvalGroups,
  }

  // Admin + Accounting read `user_roles`. Approval reads TWO independent
  // sources — `assignments` and the company master — both modeled here so
  // neither can silently pop groups in after the other resolves; `pageState`
  // is `pending` until both are loaded and `failed` if either is.
  const screenLoadState = (screen: AssignmentScreen): ReadState<unknown>[] =>
    screen === 'approval' ? [assignmentsState, companiesState] : [userRolesState]

  const term = search.trim().toLowerCase()
  const matches = (g: Group) =>
    !term ||
    g.title.toLowerCase().includes(term) ||
    g.seats.some((m) => m.name.toLowerCase().includes(term) || (m.email ?? '').toLowerCase().includes(term))

  // No screen to show — render nothing rather than defaulting to one the
  // session may not actually hold anything on (`App.tsx` also gates the tab
  // itself on `canOpenAssignments(session)`, so this is a defensive second
  // check for the resync window above, not the only one).
  if (activeScreen === null) return null

  const currentReads = screenLoadState(activeScreen)
  const currentStatus = pageState(...currentReads)
  // Approval Assignments can fail on BOTH of its sources at once, and each
  // is independently actionable (an assignments-read failure and a
  // companies-read failure point at different requests) — collect every
  // failed message rather than showing only the first and burying the other.
  const currentErrors = currentReads
    .filter((r): r is { kind: 'failed'; error: string } => r.kind === 'failed')
    .map((r) => r.error)
  const currentError = currentErrors.length > 0 ? currentErrors.join(' ') : 'Could not load this screen.'
  const currentGroups = groupsByScreen[activeScreen]
  const visible = currentGroups.filter(matches)

  // Cross-screen Find hint: when the term matches nothing on THIS screen but
  // does match a group on another VISIBLE screen, name it. Suppressed unless
  // that other screen's data is fully `ready` — `pending` data is empty and
  // would silently miss a real match, and a `failed` read means we cannot
  // know either way, so both guess in the wrong direction if allowed through.
  const otherScreenHints =
    term && visible.length === 0
      ? visibleScreens.filter(
          (sc) =>
            sc !== activeScreen &&
            pageState(...screenLoadState(sc)) === 'ready' &&
            groupsByScreen[sc].some(matches),
        )
      : []

  return (
    <div>
      <div className={s.header}>
        <ScreenTitle>Assignments</ScreenTitle>
        <Body1 className={s.sub}>Assign staff to roles to grant permissions.</Body1>
      </div>

      <ErrorBanner message={error} />

      {/* The screen selector always renders when the tab renders, even with
          only one visible screen — a fixed set of controls beats layout that
          jumps depending on who is looking. */}
      <TabList
        className={s.screenTabs}
        selectedValue={activeScreen}
        onTabSelect={(_, d) => setActiveScreen(d.value as AssignmentScreen)}
        aria-label="Assignments screens"
      >
        {visibleScreens.map((sc) => (
          <Tab key={sc} value={sc}>
            {SCREEN_TITLE[sc]}
          </Tab>
        ))}
      </TabList>

      <div className={s.controls}>
        <Field label="Find">
          <Input
            className={s.search}
            placeholder="Group, company or person…"
            value={search}
            onChange={(_, d) => setSearch(d.value)}
          />
        </Field>
        <div className={s.spacer} />
        {/* Assignment-seat furniture — renders only on Approval Assignments,
            because "retired and rejected" describes assignment rows and
            neither exists for an internal-role grant. */}
        {activeScreen === 'approval' && (
          <Checkbox
            checked={show === 'all'}
            onChange={(_, d) => setShow(d.checked ? 'all' : 'current')}
            label="Show retired and rejected"
          />
        )}
      </div>

      {currentStatus === 'pending' ? (
        <Spinner label="Loading assignments…" />
      ) : currentStatus === 'failed' ? (
        <ErrorBanner message={currentError} />
      ) : (
        <div className={s.groups}>
          {visible.length === 0 ? (
            <Body1 className={s.none}>
              No group matches “{search.trim()}”.
              {otherScreenHints.length > 0 && (
                <span className={s.hint}>
                  It matches on {otherScreenHints.map((sc) => SCREEN_TITLE[sc]).join(' and ')}.
                </span>
              )}
            </Body1>
          ) : (
            visible.map((g) => (
              <GroupCard key={g.key} group={g} busyKey={busyKey} onAppoint={openAppoint} s={s} />
            ))
          )}
        </div>
      )}

      <Dialog open={target !== null} onOpenChange={(_, d) => !d.open && setTarget(null)}>
        <DialogSurface ref={setDialogSurface}>
          <DialogBody>
            <DialogTitle>Appoint to {target?.title ?? ''}</DialogTitle>
            <DialogContent>
              <div className={s.form}>
                <ErrorBanner message={dialogError} />
                {target?.kind === 'assignment' && target.roleChoices.length > 1 && (
                  <Field label="Role">
                    <Dropdown
                      selectedOptions={[apRole]}
                      value={assignmentRoleLabel(apRole)}
                      onOptionSelect={(_, d) =>
                        setApRole((d.optionValue as AssignmentRole) ?? ASSIGNMENT_ROLES.departmentHead)
                      }
                    >
                      {target.roleChoices.map((r) => (
                        <Option key={r} value={r} text={assignmentRoleLabel(r)}>
                          {assignmentRoleLabel(r)}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>
                )}
                {target?.kind === 'assignment' && target.roleChoices.length === 1 && (
                  <Caption1>Role: {assignmentRoleLabel(target.roleChoices[0])}</Caption1>
                )}
                <Field label="Person">
                  <EntityPicker
                    placeholder="Search staff…"
                    mountNode={dialogSurface}
                    value={apUser ? { id: apUser.id, name: apUser.display_name } : undefined}
                    search={(t) =>
                      searchPeople(t).then<EntityOption[]>((people) => {
                        peopleRef.current = people
                        return people.map((u) => ({
                          id: u.id,
                          primary: u.display_name || u.upn,
                          secondary: [u.job_title, u.department].filter(Boolean).join(' · ') || u.upn,
                        }))
                      })
                    }
                    onSelect={(opt) => setApUser(peopleRef.current.find((p) => p.id === opt.id) ?? null)}
                  />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setTarget(null)} disabled={submitting}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={submitAppoint} disabled={submitting}>
                {submitting ? 'Appointing…' : 'Appoint'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}

function GroupCard({
  group,
  busyKey,
  onAppoint,
  s,
}: {
  group: Group
  busyKey: string | null
  onAppoint: (t: Target) => void
  s: ReturnType<typeof useStyles>
}) {
  const live = group.seats.filter((m) => m.live).length
  return (
    <section className={s.group}>
      <div className={s.groupHead}>
        <Subtitle2 className={s.groupTitle}>{group.title}</Subtitle2>
        <Caption1 className={s.count}>{live === 1 ? '1 person' : `${live} people`}</Caption1>
        <div className={s.spacer} />
        {group.target && (
          <Button
            appearance="subtle"
            size="small"
            icon={<PersonAddRegular />}
            onClick={() => onAppoint(group.target as Target)}
          >
            Appoint
          </Button>
        )}
      </div>
      {/* A full sentence, so it gets its own wrapping line rather than a Badge
          pill (a Badge is fixed-height and won't wrap). */}
      {group.warnEmpty && (
        <Caption1 className={s.warn}>
          <WarningRegular className={s.warnIcon} aria-hidden="true" />
          {group.warnEmpty}
        </Caption1>
      )}
      {group.blurb && <Caption1 className={s.blurb}>{group.blurb}</Caption1>}

      {group.seats.length === 0 ? (
        <Body1 className={s.none}>No one assigned.</Body1>
      ) : (
        <div className={s.members}>
          {group.seats.map((m) => (
            <div key={m.key} className={mergeClasses(s.member, !m.live && s.memberInactive)}>
              <span>{m.name}</span>
              <Caption1 className={s.email}>
                {m.email}
                {m.rejectionReason && <span className={s.reason}>{m.rejectionReason}</span>}
              </Caption1>
              <span>
                {m.roleLabel && <Badge appearance="outline">{m.roleLabel}</Badge>}
                {m.status && <> {statusBadge(m.status)}</>}
              </span>
              <span>
                {m.canRemove && m.remove && (
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<DeleteRegular className={s.trash} />}
                    aria-label={`Remove ${m.name} from ${group.title}`}
                    disabled={busyKey === m.key}
                    onClick={m.remove}
                  />
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
